/**
 * Motor de Renderização de Templates de Observação/Descrição da NFS-e
 * issue marioolinto/gspro12-app#388
 * RFC: RFC-observacao-nota-dinamica.md
 */

const { roundHalfEvenDivision } = require('./roundHalfEven.js');
const currency = require('../functions/currency.js');

const MESES = [
  'JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
  'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'
];

const TEXTO_PADRAO_DISPENSA_RETENCAO = `Para PIS/COFINS/CSLL \nÉ dispensada a retenção de igual ou inferior a R$ 10,00 exceto no DARF eletrônico emitido por meio do SIAFI (Lei nº 10.833/2003, art. 31 §3º); \nPara IR \nLEI Nº 9.430, DE 27 DE DEZEMBRO DE 1996; Dispensa de Retenção de Imposto de Renda; Art. 67. Fica dispensada a retenção de imposto de renda, de valor igual ou inferior a R$ 10,00 (dez reais);`;

/**
 * Arredondamento monetário bancário (ROUND_HALF_EVEN / Banker's Rounding)
 * consistente com Sistema Nacional NFS-e e FiscalCalculationService.
 */
function roundCurrency(valor) {
  if (valor === undefined || valor === null || valor === '' || isNaN(Number(valor))) {
    return 0;
  }
  const num = Number(valor);
  const sinal = num < 0 ? -1 : 1;
  const absNum = Math.abs(num);
  const numerador = Math.round((absNum + Number.EPSILON) * 10000);
  const cents = roundHalfEvenDivision(numerador, 100);
  return (cents / 100) * sinal;
}

/**
 * Formata data no padrão brasileiro DD/MM/AAAA.
 */
function formatarDataBr(data) {
  if (!data) return '';
  if (typeof data === 'string') {
    const s = data.trim();
    const matchIso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchIso) {
      return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
    }
    const matchBr = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (matchBr) {
      return matchBr[0];
    }
  }
  const d = new Date(data);
  if (!isNaN(d.getTime())) {
    const dia = String(d.getUTCDate()).padStart(2, '0');
    const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
    const ano = d.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
  }
  return '';
}

/**
 * Formata a competência para mês/ano por extenso em maiúsculas (ex: "SETEMBRO/2026").
 * (a) Strings "YYYY-MM-DD" ou "YYYY-MM" são lidas com getters UTC / match para evitar deslocamento de fuso;
 * (b) Quando não houver data disponível ("agora"), utiliza a competência oficial do backend (fuso da empresa).
 */
function formatarCompetenciaExtenso(dataOuCompetencia, competenciaBackend = null) {
  const entrada = dataOuCompetencia || competenciaBackend;
  if (!entrada) {
    return '';
  }

  if (typeof entrada === 'string') {
    const match = entrada.trim().match(/^(\d{4})-(\d{1,2})/);
    if (match) {
      const ano = match[1];
      const mesIndex = parseInt(match[2], 10) - 1;
      if (mesIndex >= 0 && mesIndex < 12) {
        return `${MESES[mesIndex]}/${ano}`;
      }
    }

    const d = new Date(entrada);
    if (!isNaN(d.getTime())) {
      const ano = d.getUTCFullYear();
      const mesIndex = d.getUTCMonth();
      return `${MESES[mesIndex]}/${ano}`;
    }
  }

  if (entrada instanceof Date && !isNaN(entrada.getTime())) {
    const ano = entrada.getFullYear();
    const mesIndex = entrada.getMonth();
    return `${MESES[mesIndex]}/${ano}`;
  }

  return '';
}

/**
 * Substitui placeholders {chave} no template pelos valores mapeados.
 * Se o valor for null/undefined, substitui por string vazia "".
 */
function renderTemplate(template, valores) {
  if (!template || typeof template !== 'string') {
    return '';
  }
  if (!valores || typeof valores !== 'object') {
    return template;
  }

  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, chave) => {
    if (Object.prototype.hasOwnProperty.call(valores, chave)) {
      const valor = valores[chave];
      return valor !== undefined && valor !== null ? String(valor) : '';
    }
    return '';
  });
}

/**
 * Resolve todos os placeholders transacionais, fiscais e de cadastro.
 */
function resolverPlaceholders(contexto) {
  const {
    nota = {},
    contrato = {},
    fatura = {},
    empresa = {},
    cliente = {},
    datasVencimento = [],
    templatesConfig = {},
    textoDispensaRetencao,
    somaPisCsllCofins,
    formatCurrency = currency
  } = contexto || {};

  // 1. Dados transacionais do contrato e fatura
  const contratoControle = contrato.controle ? String(contrato.controle).trim() : '';
  const contratoGarantia = contrato.garantia !== undefined && contrato.garantia !== null ? String(contrato.garantia) : '';
  const contratoId = contrato.id !== undefined && contrato.id !== null ? String(contrato.id) : '';
  const faturaParcela = fatura.parcela !== undefined && fatura.parcela !== null ? String(fatura.parcela) : '';
  const formaPagamento = contrato.forma_pagamento ? String(contrato.forma_pagamento).toUpperCase().trim() : '';

  // 2. Parcelas pré-formatadas ("dd/mm/aaaa: R$ x,xx. ")
  let parcelasTexto = '';
  if (Array.isArray(datasVencimento) && datasVencimento.length > 0) {
    for (const item of datasVencimento) {
      if (!item) continue;
      const dataStr = formatarDataBr(item.data);
      let valorItem = item.valor;
      if (valorItem === undefined || valorItem === null) {
        valorItem = (Number(nota.valor_liquido || 0) / datasVencimento.length) || 0;
      }
      const valorFormatado = formatCurrency(valorItem);
      parcelasTexto += `${dataStr}: ${valorFormatado}. `;
    }
  }

  // 3. Competência (ex: "SETEMBRO/2026") e Vencimento (dd/mm/aaaa)
  const competenciaBackend = (templatesConfig && templatesConfig.competencia) || null;
  const dataCompet = nota.data_emissao || fatura.data_competencia || competenciaBackend;
  const competencia = formatarCompetenciaExtenso(dataCompet, competenciaBackend);

  let dataVenc = '';
  if (Array.isArray(datasVencimento) && datasVencimento.length > 0 && datasVencimento[0] && datasVencimento[0].data) {
    dataVenc = datasVencimento[0].data;
  } else if (fatura.data_validade) {
    dataVenc = fatura.data_validade;
  }
  const vencimento = formatarDataBr(dataVenc);

  // 4. Tributos aproximados (= valor_bruto * aliquota_efetiva_vigente / 100, formatado com 2 casas em BRL com prefixo R$)
  let tributosAproximados = '';
  if (empresa.aliquota_efetiva_vigente !== undefined && empresa.aliquota_efetiva_vigente !== null && empresa.aliquota_efetiva_vigente !== '') {
    const aliquota = Number(empresa.aliquota_efetiva_vigente);
    const bruto = Number(nota.valor_bruto || 0);
    const valorArredondado = roundCurrency(bruto * (aliquota / 100));
    tributosAproximados = formatCurrency(valorArredondado);
  }

  // 5. Texto de dispensa de retenção
  let dispensaRetencao = '';
  if (textoDispensaRetencao !== undefined && textoDispensaRetencao !== null) {
    dispensaRetencao = String(textoDispensaRetencao);
  } else {
    const isPj = cliente && (cliente.tipo_pessoa == 2 || cliente.tipo_pessoa == '2');
    const isEmpresaNaoIsenta = empresa && (empresa.isenta_imposto == 0 || empresa.isenta_imposto === '0' || !empresa.isenta_imposto);
    if (isPj && isEmpresaNaoIsenta && somaPisCsllCofins !== undefined && Number(somaPisCsllCofins) <= 10.0) {
      dispensaRetencao = TEXTO_PADRAO_DISPENSA_RETENCAO;
    }
  }

  // 6. Chave Pix (CNPJ da empresa sem máscara)
  // NOTA: Strip síncrono via regex (\D) intencional por templateRenderer ser util síncrono puro
  // sem acesso ao singleton assíncrono $removerMascara da instância Vue (dívida técnica aceita).
  const chavePix = empresa.cnpj ? String(empresa.cnpj).replace(/\D/g, '').trim() : '';

  // 7. Texto de benefício fiscal (para qualquer tipo de cliente)
  let textoBeneficioFiscal = '';
  if (empresa.texto_beneficio_fiscal) {
    textoBeneficioFiscal = String(empresa.texto_beneficio_fiscal).trim();
  }

  return {
    contrato_controle: contratoControle,
    contrato_garantia: contratoGarantia,
    contrato_id: contratoId,
    fatura_parcela: faturaParcela,
    forma_pagamento: formaPagamento,
    parcelas: parcelasTexto,
    competencia: competencia,
    vencimento: vencimento,
    tributos_aproximados: tributosAproximados,
    texto_dispensa_retencao: dispensaRetencao,
    chave_pix: chavePix,
    texto_beneficio_fiscal: textoBeneficioFiscal
  };
}

/**
 * Validação não-negociável de configuração para emissão.
 * Bloqueia a emissão se:
 * - template_descricao_servico ou aliquota_efetiva_vigente forem nulos/vazios;
 * - empresa possuir texto_beneficio_fiscal e template_informacoes_complementares
 *   estiver vazio ou não contiver o placeholder {texto_beneficio_fiscal}.
 */
function validarConfiguracaoEmissao(templatesConfig, empresa = null, cliente = null) {
  if (!templatesConfig || !templatesConfig.template_descricao_servico || !String(templatesConfig.template_descricao_servico).trim()) {
    return {
      valido: false,
      motivo: 'A empresa prestadora não possui o Template de Descrição do Serviço (template_descricao_servico) configurado. A emissão não pode prosseguir sem template.'
    };
  }

  if (
    templatesConfig.aliquota_efetiva_vigente === null ||
    templatesConfig.aliquota_efetiva_vigente === undefined ||
    templatesConfig.aliquota_efetiva_vigente === '' ||
    isNaN(Number(templatesConfig.aliquota_efetiva_vigente))
  ) {
    const compMsg = templatesConfig.competencia ? `para a competência ${templatesConfig.competencia}` : 'para a competência atual';
    return {
      valido: false,
      motivo: `A empresa prestadora não possui alíquota efetiva cadastrada ${compMsg}. Cadastre a alíquota da competência antes de emitir a nota.`
    };
  }

  const textoBeneficio = (empresa && empresa.texto_beneficio_fiscal)
    || (templatesConfig && templatesConfig.texto_beneficio_fiscal)
    || (templatesConfig && templatesConfig.empresa && templatesConfig.empresa.texto_beneficio_fiscal);

  if (textoBeneficio && String(textoBeneficio).trim()) {
    const templateInfoCompl = templatesConfig.template_informacoes_complementares;
    if (
      !templateInfoCompl ||
      !String(templateInfoCompl).trim() ||
      !String(templateInfoCompl).includes('{texto_beneficio_fiscal}')
    ) {
      return {
        valido: false,
        motivo: 'A empresa prestadora possui Benefício Fiscal cadastrado, mas o Template de Informações Complementares está vazio ou não contém o placeholder {texto_beneficio_fiscal}. A emissão não pode prosseguir sem a configuração correta do benefício fiscal.'
      };
    }
  }

  return {
    valido: true,
    motivo: null
  };
}

const PLACEHOLDERS_VALIDOS = [
  'contrato_controle',
  'contrato_garantia',
  'contrato_id',
  'fatura_parcela',
  'forma_pagamento',
  'parcelas',
  'competencia',
  'vencimento',
  'tributos_aproximados',
  'texto_dispensa_retencao',
  'chave_pix',
  'texto_beneficio_fiscal'
];

/**
 * Identifica placeholders no formato {chave} dentro do template que não
 * pertençam à lista de placeholders reconhecidos pelo sistema.
 * @param {string} template
 * @returns {string[]} Lista de placeholders não reconhecidos encontrados (ex: ["{foo}", "{bar}"])
 */
function extrairPlaceholdersInvalidos(template) {
  if (!template || typeof template !== 'string') {
    return [];
  }
  const regex = /\{([^{}\r\n]+)\}/g;
  const invalidos = [];
  let match;
  while ((match = regex.exec(template)) !== null) {
    const raw = match[1];
    const chave = raw.trim();
    if (!PLACEHOLDERS_VALIDOS.includes(chave) || raw !== chave) {
      const placeholderCompleto = `{${raw}}`;
      if (!invalidos.includes(placeholderCompleto)) {
        invalidos.push(placeholderCompleto);
      }
    }
  }
  return invalidos;
}

const templateRenderer = {
  renderTemplate,
  resolverPlaceholders,
  formatarCompetenciaExtenso,
  formatarDataBr,
  roundCurrency,
  validarConfiguracaoEmissao,
  extrairPlaceholdersInvalidos,
  PLACEHOLDERS_VALIDOS,
  TEXTO_PADRAO_DISPENSA_RETENCAO
};

templateRenderer.default = templateRenderer;
module.exports = templateRenderer;


